/**
 * =============================================================================
 * API KEY GENERATOR COMPONENT
 * =============================================================================
 * 
 * Form component for generating API keys for the Widget or Map products.
 * Collects organization information, validates input, and generates
 * embed codes for website integration.
 * 
 * Features:
 * - Multi-step form (initial → form → success)
 * - Organization/contact information collection
 * - Content mode selection (Adult / Youth Safe)
 * - Color theme selection
 * - Terms of Service agreement
 * - Copy-to-clipboard functionality
 * - Embed code generation
 * 
 * =============================================================================
 */

import React, { useState } from 'react';
import './ApiKeyGenerator.css';

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * Smart backend URL detection.
 * Uses localhost when running locally, production URL when deployed.
 * 
 * @returns {string} The appropriate backend URL
 */
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') {
    return process.env.REACT_APP_API_URL || 'http://localhost:5005';
  } else {
    return process.env.REACT_APP_BACKEND_URL || 'https://lgbt-widget-backend.onrender.com';
  }
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * ApiKeyGenerator Component
 * 
 * Multi-step form for generating API keys and embed codes.
 * 
 * @param {object} props
 * @param {string} props.productType - 'widget' or 'map' (determines which product)
 */
const ApiKeyGenerator = ({ productType = 'widget' }) => {
  
  // ===========================================================================
  // STATE MANAGEMENT
  // ===========================================================================
  
  // Form visibility state
  const [showForm, setShowForm] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    organizationName: '',
    contactName: '',
    email: '',
    website: '',
    description: '',
    productType: productType,
    widgetMode: 'adult',
    mapMode: 'adult',
    colorTheme: 'purple',
    displayMode: 'floating',
    termsAccepted: false
  });
  
  // API key and embed code
  const [apiKey, setApiKey] = useState(null);
  const [embedCode, setEmbedCode] = useState('');
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ===========================================================================
  // THEME CONFIGURATION
  // ===========================================================================

  /**
   * Available color themes for the widget/map.
   */
  const colorThemes = [
    { id: 'purple', name: 'Pride Purple', preview: 'linear-gradient(135deg, #5CB85C 0%, #491994 100%)' },
    { id: 'inverted', name: 'Inverted', preview: 'linear-gradient(135deg, #af8bef 0%, #7DCD7D 100%)' },
    { id: 'monochrome', name: 'Monochrome', preview: 'linear-gradient(135deg, #333333 0%, #666666 100%)' },
    { id: 'ocean', name: 'Ocean Blue', preview: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)' }
  ];

  /**
   * Product display information based on type.
   */
  const productInfo = {
    widget: { name: 'Widget', icon: '🏳️‍🌈', description: 'floating button widget' },
    map: { name: 'Map', icon: '🗺️', description: 'interactive map view' }
  };

  const currentProduct = productInfo[productType] || productInfo.widget;

  // ===========================================================================
  // EVENT HANDLERS
  // ===========================================================================

  /**
   * Handles form input changes.
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /**
   * Handles content mode selection (adult/youth).
   */
  const handleModeChange = (mode) => {
    if (productType === 'widget') {
      setFormData(prev => ({ ...prev, widgetMode: mode }));
    } else {
      setFormData(prev => ({ ...prev, mapMode: mode }));
    }
  };

  /**
   * Handles color theme selection.
   */
  const handleThemeChange = (theme) => {
    setFormData(prev => ({ ...prev, colorTheme: theme }));
  };

  /**
   * Handles display mode selection (floating button vs static embed).
   * Only applicable for widget product type.
   */
  const handleDisplayModeChange = (mode) => {
    setFormData(prev => ({ ...prev, displayMode: mode }));
  };

  /**
   * Handles form submission and API key generation.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const backendUrl = getBackendUrl();
      console.log('🔗 Using backend URL:', backendUrl);
      
      // Send request to generate API key
      const response = await fetch(`${backendUrl}/api/admin/generate-key`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          productType: productType
        })
      });

      // Validate response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('❌ Response is not JSON:', contentType, 'Status:', response.status);
        throw new Error(`Server returned non-JSON response (Status: ${response.status}). Make sure your backend is running.`);
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate API key');
      }

      const data = await response.json();
      console.log('✅ API key generated successfully');
      
      // Set API key and appropriate embed code
      setApiKey(data.apiKey);
      if (productType === 'widget') {
        setEmbedCode(data.widgetEmbedCode || '');
      } else {
        setEmbedCode(data.mapEmbedCode || '');
      }
      
      setShowForm(false);
    } catch (err) {
      console.error('API key generation error:', err);
      setError(err.message || 'Failed to generate API key. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Copies text to clipboard with visual feedback.
   */
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      const button = document.activeElement;
      const originalText = button.textContent;
      button.textContent = 'Copied!';
      button.classList.add('copied');
      setTimeout(() => {
        button.textContent = originalText;
        button.classList.remove('copied');
      }, 2000);
    });
  };

  /**
   * Resets the form to initial state.
   */
  const resetForm = () => {
    setShowForm(false);
    setApiKey(null);
    setEmbedCode('');
    setFormData({
      organizationName: '',
      contactName: '',
      email: '',
      website: '',
      description: '',
      productType: productType,
      widgetMode: 'adult',
      mapMode: 'adult',
      colorTheme: 'purple',
      displayMode: 'floating',
      termsAccepted: false
    });
    setError(null);
  };

  // Get current mode based on product type
  const currentMode = productType === 'widget' ? formData.widgetMode : formData.mapMode;

  // ===========================================================================
  // RENDER
  // ===========================================================================

  return (
    <div className="api-key-generator">
      
      {/* ================================================================= */}
      {/* Initial State - Show generate button */}
      {/* ================================================================= */}
      {!showForm && !apiKey && (
        <div className="api-key-initial">
          <h3>Ready to integrate?</h3>
          <p>Get your API key to add the {currentProduct.description} to your website</p>
          <button 
            className="generate-api-key-btn"
            onClick={() => setShowForm(true)}
          >
            Generate {currentProduct.name} API Key
          </button>
        </div>
      )}

      {/* ================================================================= */}
      {/* Form State - Collect information */}
      {/* ================================================================= */}
      {showForm && !apiKey && (
        <div className="api-key-form-container">
          <h3>Get Your {currentProduct.name} API Key</h3>
          <p>We need some information to generate your key</p>
          
          <form onSubmit={handleSubmit} className="api-key-form">
            {/* Organization Name */}
            <div className="form-group">
              <label htmlFor="organizationName">Organization Name *</label>
              <input
                type="text"
                id="organizationName"
                name="organizationName"
                value={formData.organizationName}
                onChange={handleInputChange}
                placeholder="Your organization or website name"
                required
              />
            </div>

            {/* Contact Name */}
            <div className="form-group">
              <label htmlFor="contactName">Contact Name *</label>
              <input
                type="text"
                id="contactName"
                name="contactName"
                value={formData.contactName}
                onChange={handleInputChange}
                placeholder="Your name"
                required
              />
            </div>

            {/* Email */}
            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="contact@example.com"
                required
              />
            </div>

            {/* Website URL */}
            <div className="form-group">
              <label htmlFor="website">Website URL *</label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://yourwebsite.com"
                required
              />
            </div>

            {/* Description */}
            <div className="form-group">
              <label htmlFor="description">Tell us about your site/organization *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Briefly describe your organization and how you plan to use the widget..."
                maxLength={500}
                required
              />
              <span className="char-count">{formData.description.length}/500</span>
            </div>

            {/* Content Mode Selection */}
            <div className="form-group">
              <label>{currentProduct.name} Content Mode</label>
              <div className="mode-selection">
                <button
                  type="button"
                  className={`mode-btn ${currentMode === 'adult' ? 'active' : ''}`}
                  onClick={() => handleModeChange('adult')}
                >
                  <span className="mode-icon">👥</span>
                  <span className="mode-title">Adult Mode</span>
                  <span className="mode-desc">
                    {productType === 'widget' 
                      ? 'All resources including 18+ venues'
                      : 'Shows checkbox to include/exclude 18+ venues'}
                  </span>
                </button>
                <button
                  type="button"
                  className={`mode-btn ${currentMode === 'youth' ? 'active' : ''}`}
                  onClick={() => handleModeChange('youth')}
                >
                  <span className="mode-icon">🛡️</span>
                  <span className="mode-title">Youth Safe Mode</span>
                  <span className="mode-desc">
                    {productType === 'widget'
                      ? 'Filters out adult venues automatically'
                      : 'Permanently excludes all 18+ venues'}
                  </span>
                </button>
              </div>
            </div>

            {/* Color Theme Selection */}
            <div className="form-group">
              <label>Color Theme</label>
              <div className="theme-selection">
                {colorThemes.map(theme => (
                  <button
                    key={theme.id}
                    type="button"
                    className={`theme-btn ${formData.colorTheme === theme.id ? 'active' : ''}`}
                    onClick={() => handleThemeChange(theme.id)}
                  >
                    <div 
                      className="theme-preview" 
                      style={{ background: theme.preview }}
                    />
                    <span className="theme-name">{theme.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Display Mode Selection - Widget only */}
            {productType === 'widget' && (
              <div className="form-group">
                <label>Display Mode</label>
                <div className="mode-selection">
                  <button
                    type="button"
                    className={`mode-btn ${formData.displayMode === 'floating' ? 'active' : ''}`}
                    onClick={() => handleDisplayModeChange('floating')}
                  >
                    <span className="mode-icon">💬</span>
                    <span className="mode-title">Floating Widget</span>
                    <span className="mode-desc">
                      Adds a floating button to your site. Visitors click to open the resource finder.
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`mode-btn ${formData.displayMode === 'static' ? 'active' : ''}`}
                    onClick={() => handleDisplayModeChange('static')}
                  >
                    <span className="mode-icon">📋</span>
                    <span className="mode-title">Iframe Embed</span>
                    <span className="mode-desc">
                      Embeds the resource finder directly into your page. Always visible, no button needed.
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Terms of Service Checkbox */}
            <div className="form-group terms-group">
              <label className="terms-checkbox-label">
                <input
                  type="checkbox"
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    termsAccepted: e.target.checked
                  }))}
                  required
                />
                <span className="checkbox-custom"></span>
                <span className="terms-text">
                  I agree to the{' '}
                  <a 
                    href="/terms" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="terms-link"
                  >
                    Terms of Service
                  </a>
                </span>
              </label>
            </div>

            {/* Error Message */}
            {error && (
              <div className="api-key-error">
                {error}
              </div>
            )}

            {/* Form Actions */}
            <div className="form-actions">
              <button 
                type="button" 
                className="cancel-btn"
                onClick={resetForm}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="submit-btn"
                disabled={loading || !formData.termsAccepted}
              >
                {loading ? 'Generating...' : 'Generate Key'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ================================================================= */}
      {/* Success State - Show API key and embed code */}
      {/* ================================================================= */}
      {apiKey && (
        <div className="api-key-success">
          <div className="success-header">
            <span className="success-icon">✔</span>
            <h3>Your {currentProduct.name} API Key is Ready!</h3>
          </div>
          
          {/* API Key Display */}
          <div className="api-key-display">
            <label>Your API Key:</label>
            <div className="key-container">
              <code className="api-key-code">{apiKey}</code>
              <button 
                className="copy-btn"
                onClick={() => copyToClipboard(apiKey)}
                title="Copy API key"
              >
                Copy
              </button>
            </div>
          </div>

          {/* Embed Code Display */}
          {embedCode && (
            <div className="embed-code-display">
              <label>Embed Code:</label>
              <div className="code-container">
                <pre className="embed-code">{embedCode}</pre>
                <button 
                  className="copy-btn"
                  onClick={() => copyToClipboard(embedCode)}
                  title="Copy embed code"
                >
                  Copy
                </button>
              </div>
            </div>
          )}

          {/* Settings Summary */}
          <div className="widget-settings-summary">
            <h4>Your Settings:</h4>
            <ul>
              <li><strong>Product:</strong> {currentProduct.name}</li>
              <li><strong>Mode:</strong> {currentMode === 'youth' ? 'Youth Safe' : 'Adult'} Mode</li>
              <li><strong>Theme:</strong> {colorThemes.find(t => t.id === formData.colorTheme)?.name}</li>
              {productType === 'widget' && (
                <li><strong>Display:</strong> {formData.displayMode === 'static' ? 'Iframe Embed' : 'Floating Widget'}</li>
              )}
            </ul>
          </div>

          {/* ============================================================= */}
          {/* PENDING APPROVAL NOTICE */}
          {/* ============================================================= */}
          <div className="pending-approval-notice">
            <p>
              <strong>⏳ Pending Approval:</strong> Your request has been sent to our team. 
              Your embed code won't work until approved. We'll email you once the request is reviewed by our team.
            </p>
          </div>

          {/* Integration Instructions */}
          <div className="integration-instructions">
            <h4>Quick Start:</h4>
            {productType === 'widget' && formData.displayMode === 'static' ? (
              <ol>
                <li>Copy the embed code above</li>
                <li>Paste into your website's HTML where you want the resource finder to appear</li>
                <li>The resource finder will display inline on your page — no floating button, always visible</li>
                <li>You can adjust the height in the embed code to fit your layout</li>
              </ol>
            ) : (
              <ol>
                <li>Copy the embed code above</li>
                <li>Paste into your website's HTML where you want the {currentProduct.description} to appear</li>
                <li>The {currentProduct.description} will appear automatically with your chosen settings</li>
              </ol>
            )}
          </div>

          {/* Generate Another Button */}
          <button 
            className="generate-another-btn"
            onClick={resetForm}
          >
            Generate Another Key
          </button>
        </div>
      )}
    </div>
  );
};

export default ApiKeyGenerator;